import { useState } from 'react';
import { send } from '@/shared/messages';
import type { Source } from '@/shared/types';
import { prettyVideoTitle } from '@/shared/text';

export function SourcesList({
  sources,
  onChanged,
}: {
  sources: Source[];
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);

  if (sources.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500">
        No sources attached yet. Capture chapter text or a video transcript to begin.
      </div>
    );
  }

  async function remove(id: string) {
    if (!confirm('Remove this source? Generated cards/summary keep referring to its content until you regenerate.')) {
      return;
    }
    setBusyId(id);
    try {
      await send({ type: 'DELETE_SOURCE', sourceId: id });
      onChanged();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-1">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Sources ({sources.length})
      </h2>
      <ul className="space-y-1">
        {sources.map((s) => {
          const chars = (s.raw_markdown ?? '').length;
          const cueCount = s.vtt_cues?.length ?? 0;
          const empty = chars === 0 && cueCount === 0;
          return (
            <li
              key={s.id}
              className={`flex items-baseline justify-between gap-2 rounded border px-2 py-1 text-xs ${
                empty ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-card'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span
                    className={`rounded px-1 py-0.5 text-[9px] font-semibold uppercase ${
                      s.type === 'video'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {s.type}
                  </span>
                  <span className="truncate font-medium text-slate-800" title={s.title}>
                    {prettify(s.title)}
                  </span>
                </div>
                <div className="mt-0.5 text-[10px] text-slate-500">
                  {chars > 0 && <>{chars.toLocaleString()} chars</>}
                  {chars > 0 && cueCount > 0 && ' · '}
                  {cueCount > 0 && <>{cueCount} cues</>}
                  {empty && (
                    <span className="text-amber-700">
                      no transcript — capture via Videos tab
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => remove(s.id)}
                disabled={busyId === s.id}
                className="min-h-[36px] min-w-[36px] shrink-0 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                title="Remove this source"
              >
                {busyId === s.id ? '…' : '×'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const prettify = prettyVideoTitle;
